import { useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Label,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useImmer } from "use-immer";

import { TimeSeriesColors } from "@intelligems/ig-graphs";
import { GraphConfig, GraphLegend } from "@intelligems/ig-types";
import { useTheme } from "@mui/material";
import { AxisTick } from "@src/features/analytics/baseDashboard/chartComponents/AxisTick";
import { getFilterMethod } from "@src/features/analytics/baseDashboard/utils";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { BarMetricData } from "@src/types/analytics";

import { useGetTooltipProps } from "../../hooks/use-get-tooltip-props";
import { InteractiveChartLegend } from "../../legends/InteractiveChartLegend";
import { BarLabel } from "../chartComponents/BarLabel";

const CustomLabel = () => {
  const theme = useTheme();

  return (
    <Label
      style={{
        fill: theme.palette.text.primary,
        fontFamily: theme.typography.fontFamily,
        fontSize: 12,
        marginTop: "10px",
        textAnchor: "middle",
      }}
      angle={270}
      position={"insideLeft"}
      offset={10}
      textAnchor={"middle"}
    />
  );
};

interface IgComposedChartProps {
  config: GraphConfig;
  filterMethod?: string;
  focusGroups?: string[];
  groupMetricData: BarMetricData[];
  legends?: GraphLegend[];
  syncId?: string;
  dateFormat?: string;
  graphHeight?: number;
}

export const OrderValueChart = ({
  config,
  filterMethod,
  focusGroups,
  groupMetricData,
  legends,
  syncId,
  dateFormat,
  graphHeight,
}: IgComposedChartProps) => {
  const graphValueFormatter = useGraphValueFormatter();
  const [baseFocusGroups, setBaseFocusGroups] = useImmer<string[]>([]);
  const tooltipProps = useGetTooltipProps({
    legends: legends?.filter((line) => !baseFocusGroups.includes(line.id)),
    dateFormat,
  });
  const theme = useTheme();
  const focusGroupedData = useMemo(() => {
    if (!focusGroups || focusGroups.length === 0) {
      return groupMetricData;
    } else {
      return groupMetricData.filter(
        (groupData) => !focusGroups.includes(groupData.variation_id)
      );
    }
  }, [focusGroups, groupMetricData]);

  const filteredData = filterMethod
    ? getFilterMethod(focusGroupedData, filterMethod)
    : focusGroupedData;

  const allData =
    !filterMethod || filterMethod === "none" ? focusGroupedData : filteredData;

  const IS_CHART_OVER_SATURATED = allData.length > 30;

  const handleLegendClick = (dataKey: string) => {
    if (baseFocusGroups.includes(dataKey)) {
      setBaseFocusGroups(baseFocusGroups.filter((el) => el !== dataKey));
    } else {
      setBaseFocusGroups((prev) => [...prev, dataKey]);
    }
  };

  const barMaxData = allData.reduce((accumulator, currentValue) => {
    return Math.max(accumulator, currentValue["n_orders"]);
  }, 0);
  const barPaddedMaxData = barMaxData * 1.75;
  const lines = legends?.filter((legend) => legend.id !== "n_orders");
  const nOrdersIndexInLegends =
    legends?.findIndex((legend) => legend.id === "n_orders") || 0;

  return (
    <ResponsiveContainer
      height={graphHeight ?? (config.isImpactGraph ? 150 : 300)}
      width="100%"
    >
      <ComposedChart
        width={730}
        height={250}
        data={allData}
        margin={{
          top: 5,
          left: -20,
        }}
        syncId={syncId}
      >
        <XAxis
          tick={(props) => (
            <AxisTick
              config={config}
              graphValueFormatter={graphValueFormatter}
              groupMetricData={allData}
              dateFormat={dateFormat}
              {...props}
            />
          )}
          axisLine={{ stroke: theme.igBorders.core.disabled }}
          tickLine={false}
          dataKey={"xAxisKey"}
          padding={{ right: 25 }}
        />

        {/* Left Y-axis for bars */}
        <YAxis
          axisLine={false}
          domain={[0, barPaddedMaxData]}
          interval={0}
          label={CustomLabel}
          style={{ fontFamily: theme.typography.fontFamily, fontSize: 10 }}
          tickFormatter={(value: any) => {
            return graphValueFormatter({
              isAxisTick: true,
              value,
              configDataTypes: ["numberNoDecimal"],
              shouldShorten: true,
            })!;
          }}
          yAxisId="left"
        />

        {/* Right Y-axis for lines */}
        <YAxis
          axisLine={false}
          interval={0}
          label={CustomLabel}
          orientation="right"
          style={{ fontFamily: theme.typography.fontFamily, fontSize: 10 }}
          tickFormatter={(value: any) => {
            return graphValueFormatter({
              isAxisTick: true,
              value,
              configDataTypes: ["currencyNoDecimal"],
              shouldShorten: true,
            })!;
          }}
          tickLine={false}
          yAxisId="right"
        />

        <Tooltip {...tooltipProps} />

        <CartesianGrid stroke={theme.igPrimitives.gray[300]} />
        <Bar
          isAnimationActive={false}
          dataKey="n_orders"
          hide={baseFocusGroups.includes("n_orders")}
          name={config.title?.[0] || ""}
          radius={[3, 3, 0, 0]}
          yAxisId="left"
        >
          <LabelList
            content={(props: any) => {
              if (IS_CHART_OVER_SATURATED) return;

              return (
                // @ts-ignore
                <BarLabel
                  props={props}
                  config={config}
                  formattedValue={graphValueFormatter({
                    isAxisTick: true,
                    value: props.value,
                    configDataTypes: ["numberNoRounding"],
                  })}
                />
              );
            }}
            formatter={(value) =>
              graphValueFormatter({
                isAxisTick: true,
                value,
                configDataTypes: ["numberNoDecimal"],
              })!
            }
          />

          {allData?.map((group: BarMetricData, index: number) => (
            <Cell
              key={`cell-${index}`}
              fill={TimeSeriesColors[nOrdersIndexInLegends]}
            />
          ))}
        </Bar>

        {lines?.map((legend, index) => (
          <Line
            connectNulls={false}
            dataKey={legend.id}
            dot={false}
            hide={baseFocusGroups.includes(legend.id)}
            key={index}
            stroke={TimeSeriesColors[index + 1]}
            strokeWidth={1.5}
            type="monotone"
            yAxisId="right"
          />
        ))}

        {config.showLegend && legends && (
          <Legend
            content={
              <InteractiveChartLegend
                onLegendClick={handleLegendClick}
                focusGroups={baseFocusGroups}
                lines={legends}
              />
            }
            wrapperStyle={{ paddingTop: 24 }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
